import React, { useState, useMemo } from 'react';
import { inject, observer } from 'mobx-react';
import compose from 'helpers/compose';
import { useTranslation } from 'react-i18next';

import GetAppIcon from '@material-ui/icons/GetApp';
import Visibility from '@material-ui/icons/Visibility';
import CheckIcon from '@material-ui/icons/Check';
import { CircularProgress } from '@material-ui/core';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import MobileFriendlyIcon from '@material-ui/icons/MobileFriendly';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import FindInPageOutlinedIcon from '@material-ui/icons/FindInPageOutlined';
import SystemUpdateOutlinedIcon from '@material-ui/icons/SystemUpdateOutlined';

import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog';

import { Request } from 'apps/Pdf/templates/Request/Request';
import { ContractTermination } from 'apps/Pdf/templates/ContractTermination/ContractTermination';
import { ContractAmendment } from 'apps/Pdf/templates/ContractAmendment/ContractAmendment';

import { RootStore } from 'stores/rootStore';
import { SignatureStore } from 'stores/account/signatureStore';
import PdfPreview from 'components/PdfPreview/PdfPreview';

import useStyles from 'pages/Accounts/Detail/Signature/styles/Documents';

export interface Params {
  accountId: string;
}

interface Props {
  rootStore: RootStore;
  store: SignatureStore;
}

const showPDF = (
  rootStore: RootStore,
  accountId: string,
  pdfType?: string,
  requestId?: string,
  afterLoad?: () => void,
  onError?: () => void,
) => {
  switch (pdfType) {
    case 'CONTRACT_TERMINATION':
      return (
        <ContractTermination
          accountId={accountId}
          docId={requestId}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    case 'CONTRACT_AMENDMENT':
      return (
        <ContractAmendment
          accountId={accountId}
          docId={requestId}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
    default:
      return (
        <Request
          accountId={accountId}
          docId={requestId}
          rootStore={rootStore}
          afterLoad={afterLoad}
          onError={onError}
          preview
        />
      );
  }
};

const SignatureDocuments: React.FC<Props> = ({ rootStore, store }) => {
  const { t } = useTranslation();

  const [pdfOpen, setPdfOpen] = useState(false);
  const [pdfType, setPdfType] = useState<string | undefined>(undefined);
  const [requestId, setRequestId] = useState<string | undefined>(undefined);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [confirmType, setConfirmType] = useState('');
  const [confirmAction, setConfirmAction] = useState<'CONFIRM' | 'CANCEL'>('CONFIRM');

  const classes = useStyles();


  // EDS-4915
  const anOfferYouCannotRefuse = store?.documents?.some(d => d.type === 'MEETING_RECORD');

  const mobile = useMediaQuery('(max-width:640px)', { noSsr: true });

  const pdfPreview = useMemo(
    () => (
      <PdfPreview
        open={pdfOpen}
        handleClose={() => {
          setPdfOpen(false);
          setPdfType(undefined);
        }}
        miscFunction={() => {
          pdfType && setConfirmType(pdfType);
          setConfirmDialogOpen(true);
        }}
        miscText="general_confirm"
        miscIcon={<CheckIcon />}
        miscMobileIcon={<MobileFriendlyIcon />}
        renderTemplate={
          pdfType && requestId
            ? (afterLoad: undefined) =>
                showPDF(rootStore, store.accountId, pdfType, requestId, afterLoad, () =>
                  rootStore.stores.uiStore.stopLoading('pdfPreview'),
                )
            : () => null
        }
      />
    ),
    [pdfType, pdfOpen, requestId, rootStore, store.accountId],
  );

  return (
    <Container maxWidth="md">
      {store.requests && store.requests.length > 0 && (
        <>
          <Typography variant="h1" className={classes.title}>
            {t('requests_title')}
          </Typography>

          <Grid container className={classes.docs} spacing={0}>
            {store.requests.map((doc) => (
              <Grid container item xs={12} alignItems="center" className={classes.docLine} key={doc.type} spacing={0}>
                <Grid item container xs={7} sm={9}>
                  <Grid item xs={12} sm={6}>
                    {t(`pdf_document_${doc.type}`)}
                  </Grid>
                  <Grid container item xs={12} sm={6} className={classes.actions}>
                    <Button
                      variant={mobile ? 'text' : 'outlined'}
                      color={mobile ? 'primary' : undefined}
                      startIcon={mobile ? <FindInPageOutlinedIcon fontSize="large" /> : <Visibility />}
                      size={mobile ? 'large' : 'medium'}
                      onClick={() => {
                        rootStore.stores.uiStore.startLoading('pdfPreview');
                        setPdfType(doc.type);
                        setRequestId(doc.id);
                        setConfirmAction('CONFIRM');
                        setPdfOpen(true);
                      }}
                      className={classes.viewButton}
                    >
                      {!mobile && t('general_preview')}
                    </Button>
                    <Button
                      variant={mobile ? 'text' : 'outlined'}
                      color={mobile ? 'primary' : undefined}
                      size={mobile ? 'large' : 'medium'}
                      startIcon={
                        doc.loading ? (
                          <CircularProgress size={mobile ? 28 : 20} />
                        ) : mobile ? (
                          <SystemUpdateOutlinedIcon fontSize="large" />
                        ) : (
                          <GetAppIcon />
                        )
                      }
                      onClick={() => store.downloadDocument(doc.type, true, doc.id)}
                      className={classes.viewButton}
                    >
                      {!mobile && t('general_pdf')}
                    </Button>
                  </Grid>
                </Grid>
                <Grid item xs={5} sm={3} className={classes.requestActions}>
                  {!(anOfferYouCannotRefuse && doc.type === 'CONTRACT_AMENDMENT') &&
                  <Button
                    variant="contained"
                    color="default"
                    onClick={() => {
                      setConfirmType(doc.type);
                      setRequestId(doc.id);
                      setConfirmAction('CANCEL');
                      setConfirmDialogOpen(true);
                    }}
                    className={classes.confirmButton}
                  >
                    {t('general_reject')}
                  </Button>}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                      setConfirmType(doc.type);
                      setRequestId(doc.id);
                      setConfirmAction('CONFIRM');
                      setConfirmDialogOpen(true);
                    }}
                    className={classes.confirmButton}
                  >
                    {t('general_confirm')}
                  </Button>
                </Grid>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {pdfPreview}

      <ConfirmDialog
        isOpen={confirmDialogOpen}
        title={confirmAction === 'CONFIRM' ? 'general_confirm' : 'general_reject'}
        close={() => setConfirmDialogOpen(false)}
        confirm={async () => {
          if (requestId) {
            setConfirmDialogOpen(false);
            setPdfOpen(false);
            await store.confirmRequest(requestId, confirmAction);
          }
        }}
        btnConfirmText="general_confirm"
      >
        <Typography variant="h3">{t(`pdf_document_${confirmType}`)}</Typography>
        <Typography>{t(`document_request_info_${confirmAction}`)}</Typography>
      </ConfirmDialog>
    </Container>
  );
};

export default compose(inject('rootStore'), observer)(SignatureDocuments);
